import { CalendarDaysIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { t, fmt, getAppLocale } from '@/i18n';
import type { CalendarEvent } from './Calendar';
import { CapacityBar, confirmationDisplay } from './eventCapacity';

interface UpcomingListProps {
  events: CalendarEvent[];
  /** Link target for a card. Defaults to the public event page `/<slug>/`. */
  getEventHref?: (event: CalendarEvent) => string;
}

const MS_DAY = 86_400_000;

function monthBucket(d: Date): string {
  return d.toLocaleDateString(getAppLocale(), { month: 'long', year: 'numeric' });
}

function formatDay(d: Date): string {
  return d.toLocaleDateString(getAppLocale(), { day: 'numeric', month: 'short' });
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString(getAppLocale(), { hour: '2-digit', minute: '2-digit' });
}

/** "21 Jun · 08:00–12:00" for a single day, "21 Jun → 30 Jun 2027" across days. */
function formatRange(startStr: string, endStr: string | null): string {
  const start = new Date(startStr);
  const end = endStr ? new Date(endStr) : start;
  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) {
    return `${formatDay(start)} · ${formatTime(start)}–${formatTime(end)}`;
  }
  return `${formatDay(start)} → ${formatDay(end)} ${end.getFullYear()}`;
}

function daysUntil(startStr: string): string {
  const n = Math.max(0, Math.round((new Date(startStr).getTime() - Date.now()) / MS_DAY));
  if (n === 0) return t('today');
  if (n < 31) return fmt('in %(count)s days', { count: n });
  return fmt('in ~%(count)s mo', { count: Math.round(n / 30) });
}

/**
 * List of upcoming events grouped by month — the list-first surface of the public
 * organizer page (issue #398). Each card shows a date badge, confirmation status,
 * date/time range, venue, capacity bar (with min tick) and a Register/Waitlist CTA.
 */
export default function UpcomingList({ events, getEventHref }: UpcomingListProps) {
  const upcoming = events
    .filter((ev) => ev.start_at)
    .sort((a, b) => new Date(a.start_at!).getTime() - new Date(b.start_at!).getTime());

  if (upcoming.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 px-6 py-16 dark:border-gray-700">
        <CalendarDaysIcon className="h-10 w-10 text-gray-400 dark:text-gray-600" />
        <h3 className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
          {t('No upcoming trips')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
          {t('Check back later for new trips.')}
        </p>
      </div>
    );
  }

  const hrefFor = getEventHref ?? ((ev: CalendarEvent) => `/${ev.slug}/`);

  let lastBucket: string | null = null;

  return (
    <div>
      {upcoming.map((ev) => {
        const start = new Date(ev.start_at!);
        const bucket = monthBucket(start);
        const showHeader = bucket !== lastBucket;
        lastBucket = bucket;
        const cs = confirmationDisplay(ev);
        const isFull =
          ev.max_capacity != null && ev.max_capacity > 0 && (ev.confirmed_count ?? 0) >= ev.max_capacity;

        return (
          <div key={ev.id}>
            {showHeader && (
              <div className="mb-3 mt-7 first:mt-0">
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-teal-600 dark:text-teal-400">
                  {bucket}
                </span>
              </div>
            )}
            <a
              href={hrefFor(ev)}
              className="mb-3 block rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-teal-300 hover:shadow-md dark:border-gray-800 dark:bg-gray-950 dark:hover:border-teal-500/50 sm:p-5"
            >
              <div className="flex items-start gap-4 sm:gap-5">
                {/* Date badge */}
                <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-teal-50 dark:bg-teal-500/10 sm:h-16 sm:w-16">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-600 dark:text-teal-300">
                    {start.toLocaleDateString(getAppLocale(), { month: 'short' })}
                  </span>
                  <span className="text-xl font-bold leading-none text-teal-700 dark:text-teal-300 sm:text-2xl">
                    {start.getDate()}
                  </span>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cs.chip}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${cs.dot}`} />
                      {cs.label}
                    </span>
                    <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
                      {daysUntil(ev.start_at!)}
                    </span>
                  </div>

                  <h3 className="mt-2 truncate text-lg font-bold text-gray-900 dark:text-white">{ev.title}</h3>

                  <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDaysIcon className="h-3.5 w-3.5" />
                      {formatRange(ev.start_at!, ev.end_at)}
                    </span>
                    {ev.venue_details && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPinIcon className="h-3.5 w-3.5" />
                        <span className="truncate max-w-[200px]">{ev.venue_details}</span>
                      </span>
                    )}
                  </div>

                  <div className="mt-3.5 max-w-xs">
                    <CapacityBar event={ev} size="lg" />
                  </div>
                </div>

                <div className="hidden shrink-0 self-center sm:block">
                  <span className="rounded-full bg-teal-600 px-4 py-2 text-xs font-semibold text-white">
                    {isFull ? t('Waitlist') : t('Register')} →
                  </span>
                </div>
              </div>
            </a>
          </div>
        );
      })}
    </div>
  );
}
